<template>
  <div class="users-page">
    <Head title="Users" />

    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Administration</p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">Users</h1>
        <p class="mt-1.5 text-sm text-gray-500">Manage account access, roles, and agency scopes.</p>
      </div>
      <Link
        href="/users/create"
        class="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
      >
        Create User
      </Link>
    </div>

    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total users</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ formatNumber(users.length) }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Admins</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ adminCount }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Agency</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ agencyCount }}</p>
      </div>
    </div>

    <div class="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div class="mb-4">
        <h2 class="text-sm font-bold text-brand-900">Filters</h2>
        <p class="text-xs text-gray-500">Search by name/email and filter by role</p>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div class="md:col-span-2">
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Search</label>
          <input
            v-model="form.search"
            type="text"
            placeholder="Search by name or email..."
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Role</label>
          <select
            v-model="form.role"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All</option>
            <option v-for="(label, value) in roles" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Trashed</label>
          <select
            v-model="form.trashed"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">Active only</option>
            <option value="with">With trashed</option>
            <option value="only">Trashed only</option>
          </select>
        </div>
      </div>
      <div class="mt-4 flex justify-end">
        <button
          type="button"
          class="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          @click="reset"
        >
          Clear filters
        </button>
      </div>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="border-b border-gray-100 px-5 py-3">
        <h2 class="text-sm font-bold text-brand-900">User list</h2>
        <p class="text-xs text-gray-500">Showing {{ users.length }} users</p>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full">
          <thead>
            <tr class="border-b border-gray-100 bg-brand-50/60 text-left">
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Name</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Email</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Role</th>
              <th class="relative px-4 py-3"><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in users"
              :key="user.id"
              class="border-b border-gray-50 transition-colors hover:bg-brand-50/40"
            >
              <td class="px-4 py-3.5">
                <Link
                  :href="`/users/${user.id}/edit`"
                  class="flex items-center gap-3 text-sm font-semibold text-brand-700 hover:text-brand-900"
                >
                  <img
                    v-if="user.photo"
                    class="h-9 w-9 rounded-xl object-cover ring-1 ring-gray-200"
                    :src="user.photo"
                    :alt="user.name"
                  />
                  <span
                    v-else
                    class="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-600 text-[11px] font-bold text-white"
                  >
                    {{ initials(user.name) }}
                  </span>
                  <span :class="{ 'line-through text-gray-400': user.deleted_at }">{{ user.name }}</span>
                </Link>
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">{{ user.email }}</td>
              <td class="whitespace-nowrap px-4 py-3.5">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  :class="user.role === 'agency' ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700'"
                >
                  {{ roles[user.role] || user.role }}
                </span>
                <span
                  v-if="user.owner"
                  class="ml-1 inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600"
                >
                  Owner
                </span>
              </td>
              <td class="px-4 py-3.5 text-right">
                <Link
                  :href="`/users/${user.id}/edit`"
                  class="inline-flex items-center rounded-lg p-1.5 text-gray-400 transition hover:bg-brand-50 hover:text-brand-700"
                  title="Edit"
                >
                  <icon name="cheveron-right" class="h-5 w-5" />
                </Link>
              </td>
            </tr>
            <tr v-if="!users.length">
              <td colspan="4" class="px-4 py-16 text-center">
                <p class="text-base font-bold text-brand-900">No users found</p>
                <p class="mt-1 text-sm text-gray-500">Try adjusting filters or create a new user.</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'

export default {
  components: {
    Head,
    Icon,
    Link,
  },
  layout: Layout,
  props: {
    filters: Object,
    users: Array,
    roles: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search || '',
        role: this.filters.role || '',
        trashed: this.filters.trashed || '',
      },
    }
  },
  computed: {
    adminCount() {
      return (this.users || []).filter((u) => u.role !== 'agency').length
    },
    agencyCount() {
      return (this.users || []).filter((u) => u.role === 'agency').length
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        const payload = pickBy(this.form, (v) => v !== '' && v != null)
        this.$inertia.get('/users', payload, { preserveState: true })
      }, 300),
    },
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString('id-ID')
    },
    initials(name) {
      return String(name || 'U')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0])
        .join('')
        .toUpperCase()
    },
    reset() {
      this.form = mapValues(this.form, () => '')
    },
  },
}
</script>
